import { FC, useMemo, useState } from "react";
import { LocationGroupType } from "@src/types.generated";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import { locationGroupTypeDisplayName } from "@src/features/home/constants";
import Text from "@src/lib/ui/Text";
import { LocationGroupThinFragment } from "@src/features/locations/hooks/useLocationGroups.generated";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";

type Props = {
  type: LocationGroupType;
  label?: string;
  placeholder?: string;
  locationGroupId?: string;
  setLocationGroupId: (x: string | undefined) => void;
  locationGroupOptions?: LocationGroupThinFragment[];
};

export const LocationGroupSingleSelectField: FC<Props> = (props) => {
  const { lgTypeToLgs } = useLocationGroups();
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    return (props.locationGroupOptions || lgTypeToLgs[props.type]).map((x) => ({
      value: x.id,
      label: x.name,
    }));
  }, [lgTypeToLgs, props.locationGroupOptions, props.type]);
  const label: string = useMemo(() => {
    if (props.label) return props.label;
    else {
      return locationGroupTypeDisplayName[props.type];
    }
  }, [props.label, props.type]);
  const selectedName = useMemo(() => {
    return options.find((o) => o.value === props.locationGroupId)?.label;
  }, [options, props.locationGroupId]);
  return (
    <div className="flex flex-col gap-1 self-stretch">
      <div className="flex justify-between self-stretch">
        <Text variant="fine-print-title" className="text-black">
          {label}
        </Text>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex w-full justify-between px-3"
          >
            <span
              className={selectedName ? "truncate" : "truncate text-gray-5"}
            >
              {selectedName || props.placeholder || `Select a ${label}...`}
            </span>
            <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          disablePortal
        >
          <Command>
            <CommandInput placeholder="Search..." className="h-9" />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    props.setLocationGroupId(undefined);
                    setOpen(false);
                  }}
                  className="text-gray-7"
                >
                  None
                </CommandItem>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      props.setLocationGroupId(option.value);
                      setOpen(false);
                    }}
                  >
                    <Text variant="compact-description">{option.label}</Text>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LocationGroupSingleSelectField;
